'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef, type ReactNode } from 'react';

import s from './Chip.module.css';

export const chipVariantOptions = ['default', 'selection'] as const;
export type ChipVariant = (typeof chipVariantOptions)[number];

export type ChipBaseProps = {
  ariaDisabled?: boolean;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  isSelected?: boolean;
  variant?: ChipVariant;
};

export type ChipProps = ChipBaseProps &
  ComponentPropsWithRef<'button'> & {
    asChild?: boolean;
    isReadOnly?: boolean;
  };

export const chipVariants = cva(s.chip, {
  variants: {
    variant: {
      default: s.defaultVariant,
      selection: s.selectionVariant,
    },
    isReadOnly: {
      true: s.isReadOnly,
    },
    isSelected: {
      true: s.isSelected,
    },
  },
});

export const Chip = (props: ChipProps) => {
  const {
    ariaDisabled,
    asChild = false,
    children,
    className,
    disabled = false,
    isReadOnly = false,
    isSelected,
    onClick,
    variant = 'default',
    ...attrs
  } = props;

  if (!children) {
    return null;
  }

  const handleClick: ComponentPropsWithRef<'button'>['onClick'] = (e) => {
    if (disabled || isReadOnly) {
      e.preventDefault();
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  const Component = asChild ? Slot : isReadOnly ? 'span' : 'button';
  const appearsDisabled = ariaDisabled || disabled ? true : undefined;

  return (
    <Component
      aria-disabled={appearsDisabled}
      className={clsx(
        chipVariants({ variant, isReadOnly, isSelected }),
        className,
      )}
      data-selected={isSelected}
      disabled={disabled}
      onClick={handleClick}
      {...attrs}
    >
      {children}
    </Component>
  );
};
